import { FormEvent, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { CreateAccountForm } from "@/components/shell/CreateAccountForm";
import { LoginCard } from "@/components/shell/LoginCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ApiError, api } from "@/lib/api";
import { rememberAccountId, type WorkspaceAccount } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";
import { invitationAcceptPath } from "@/lib/invitation-share";
import { parseInviteTokenInput } from "@/lib/invite-token";
import { cn } from "@/lib/utils";

type Mode = "create" | "join";

export default function AccountOnboardingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("create");
  const [accountsStatus, setAccountsStatus] = useState<"loading" | "empty" | "has">("loading");
  const [inviteInput, setInviteInput] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .fetch<{ accounts: WorkspaceAccount[] }>("/accounts")
      .then(({ accounts }) => {
        if (cancelled) return;
        setAccountsStatus(accounts.length === 0 ? "empty" : "has");
      })
      .catch(() => {
        if (!cancelled) setAccountsStatus("empty");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (accountsStatus === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-muted/30 text-sm text-muted-foreground">
        正在确认账户状态…
      </main>
    );
  }

  if (accountsStatus === "has") {
    return <Navigate to="/" replace />;
  }

  async function handleCreated(account: WorkspaceAccount) {
    rememberAccountId(account.id);
    navigate(`/accounts/${account.id}`, { replace: true });
  }

  function handleJoin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setInviteError(null);
    const token = parseInviteTokenInput(inviteInput);
    if (!token) {
      setInviteError("请粘贴有效的邀请链接，或输入邀请码");
      return;
    }
    navigate(invitationAcceptPath(token));
  }

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }

  const label = user?.email || user?.phone || user?.wechat_union_id || `用户 #${user?.id}`;

  return (
    <LoginCard
      title="创建或加入商家账户"
      subtitle="你还没有可进入的账户。创建后你将成为所有者，或通过邀请加入已有团队。"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-2 text-sm font-medium transition-colors",
              mode === "create"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setMode("create")}
          >
            创建账户
          </button>
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-2 text-sm font-medium transition-colors",
              mode === "join"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setMode("join")}
          >
            加入账户
          </button>
        </div>

        {mode === "create" ? (
          <CreateAccountForm
            idPrefix="onboarding-account"
            active={mode === "create"}
            onSuccess={handleCreated}
          />
        ) : (
          <form className="space-y-4" onSubmit={handleJoin} noValidate>
            <div className="space-y-2">
              <Label htmlFor="onboarding-invite">邀请链接或邀请码</Label>
              <Input
                id="onboarding-invite"
                value={inviteInput}
                onChange={(event) => setInviteInput(event.target.value)}
                placeholder="粘贴邀请链接，或输入邀请码"
                autoFocus
                aria-invalid={Boolean(inviteError)}
              />
              {inviteError ? (
                <p className="text-sm text-destructive" role="alert">
                  {inviteError}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  打开邀请链接后将确认并加入对应账户。
                </p>
              )}
            </div>
            <Button type="submit" className="w-full">
              继续加入
            </Button>
          </form>
        )}

        <Separator />

        <div className="flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
          <p className="truncate max-w-full">当前登录：{label}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={loggingOut}
            onClick={() => void handleLogout()}
          >
            {loggingOut ? "退出中…" : "退出登录"}
          </Button>
        </div>
      </div>
    </LoginCard>
  );
}
